import { captureEvent } from "@Light/app/analytics";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import { clsx } from "clsx";
import { useState } from "react";

type ErrorMessageProps = {
  children: React.ReactNode;
};

const ErrorMessage = ({ children }: ErrorMessageProps) => {
  return (
    <div className="text-red-600 text-sm">
      {children}{" "}
      <button
        onClick={() => {
          window.location.reload();
        }}
        className="underline"
      >
        Try again
      </button>
    </div>
  );
};

export type BatteryManufacturerSelectionProps = {
  onComplete?: (reason: string) => void;
};

export function BatteryManufacturerSelection({}: BatteryManufacturerSelectionProps) {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const Select = scaffold.system.select;
  const { useGenerateDeviceLinkUrlMutation, useGetBatteryManufacturersQuery } =
    useLight();

  const {
    data: manufacturers,
    isLoading: isLoadingManufacturers,
    isError: isManufacturersError,
  } = useGetBatteryManufacturersQuery();

  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");

  const [generateDeviceLink, generateDeviceLinkResult] =
    useGenerateDeviceLinkUrlMutation();

  const isButtonDisabled =
    !selectedManufacturer ||
    isLoadingManufacturers ||
    generateDeviceLinkResult.isLoading ||
    !generateDeviceLinkResult.data?.connect_link;

  return (
    <PageBody
      title="What type of battery are you using?"
      footerAfterContent={false}
      footer={
        <ButtonFooter>
          <Button
            size="lg"
            onClick={() => {
              captureEvent("device_manufacturer_selected", {
                device_type: "battery",
                manufacturer: selectedManufacturer,
              });
              window.open(
                generateDeviceLinkResult.data?.connect_link || "",
                "_blank",
                "noopener,noreferrer",
              );
            }}
            disabled={isButtonDisabled}
            className="w-full"
          >
            Connect
          </Button>
        </ButtonFooter>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          value={selectedManufacturer}
          onChange={async (e) => {
            const manufacturer = e.target.value;
            setSelectedManufacturer(manufacturer);
            await generateDeviceLink({ manufacturer });
          }}
          className={clsx(
            "w-full",
            !selectedManufacturer && "text-gray-400 font-normal",
          )}
          disabled={isLoadingManufacturers || isManufacturersError}
        >
          <option value="" disabled className="text-gray-400 font-normal">
            {isLoadingManufacturers
              ? "Loading manufacturers..."
              : "Battery brand"}
          </option>
          {manufacturers?.map((manufacturer) => (
            <option key={manufacturer.name} value={manufacturer.name}>
              {manufacturer.display_name}
            </option>
          ))}
        </Select>
        {isManufacturersError && (
          <ErrorMessage>We were unable to load battery brands.</ErrorMessage>
        )}
        {generateDeviceLinkResult.isError && (
          <ErrorMessage>
            We were unable to connect you to your manufacturer to link your
            device.
          </ErrorMessage>
        )}
      </div>
    </PageBody>
  );
}
